import type { OnServerDataMessageArgs } from '@azure/web-pubsub-client';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AlertDto } from '~/api/generated-api';
import { useGetAlertsAndGroupsForCurrentUserQuery } from '~/api/generated-api';
import { useAppDispatch, useAppSelect } from '~/app/store';
import usePubSubSocket from '~/common/hooks/PubSub/usePubSubSocket';
import { EMER_FLAG } from '~/common/models/featureFlags.model';
import {
  addAlert,
  removeAlerts,
  replaceAlerts,
  setAlerts,
} from '~/store/alerts/alertsSlice';
import { selectAlerts } from '~/store/alerts/alertsSlice.selectors';
import { selectActingProfileOrganization } from '~/store/profile/profileSlice.selectors';
import {
  isEmerAlertEvent,
  isEmerAlertRecord,
} from '~/utils/alerts/emerAlerts.utils';

const BADGE_PAGE_SIZE = 20;

type BadgeCursor = {
  orgId: string;
  isEmerEnabled: boolean;
  /* Page 1 fetch this cursor was paged from */
  firstPageFulfilledAt: number;
  token: string;
  /* Visible alerts seen on the pages before this token */
  visibleCount: number;
};

/* Alert ids the badge stored from fetched pages, and the org they belong to */
type StoredWindow = { orgId?: string; ids: string[] };

/**
 * Keeps the alerts store behind the header badge in sync with the alerts API
 * and the real-time socket. With the EMER flag off, EMER alerts are kept out
 * of the store and the badge pages past them until it has seen a full page of
 * visible alerts, rebuilding that window from page 1 whenever the org, the
 * flag, or page 1 itself changes.
 */
export const useBadgeAlerts = (): void => {
  const dispatch = useAppDispatch();
  const [storedAlerts] = useAppSelect(selectAlerts);
  const orgId = useAppSelect(selectActingProfileOrganization)?.id;
  const { getFlag } = useFeatureFlags();
  const isEmerEnabled = Boolean(getFlag(EMER_FLAG));

  const baseArgs = {
    size: BADGE_PAGE_SIZE,
    'cs-organization': orgId ?? '',
    dismissed: false,
  };
  const firstPage = useGetAlertsAndGroupsForCurrentUserQuery(baseArgs, {
    skip: !orgId,
    refetchOnMountOrArgChange: true,
  });

  const [badgeCursor, setBadgeCursor] = useState<BadgeCursor>();
  const storedWindowRef = useRef<StoredWindow>({ ids: [] });
  // A refetch of page 1 (cache invalidation or org switch) moves its timestamp and discards the pages built on it.
  const cursor =
    badgeCursor &&
    badgeCursor.orgId === orgId &&
    badgeCursor.isEmerEnabled === isEmerEnabled &&
    badgeCursor.firstPageFulfilledAt === firstPage.fulfilledTimeStamp
      ? badgeCursor
      : undefined;

  const laterPage = useGetAlertsAndGroupsForCurrentUserQuery(
    { ...baseArgs, 'cs-continuation-token': cursor?.token },
    { skip: !orgId || !cursor, refetchOnMountOrArgChange: true }
  );
  const { data, isLoading, isFetching } = cursor ? laterPage : firstPage;
  const firstPageFulfilledAt = firstPage.fulfilledTimeStamp;

  const handleAddAlert = useCallback(
    (event: OnServerDataMessageArgs) => {
      const { message } = event;
      if (typeof message.data === 'object' && 'data' in message.data) {
        const alert = message.data.data as AlertDto;
        if (!isEmerEnabled && isEmerAlertEvent(alert?.event?.event)) {
          return;
        }
        dispatch(addAlert(alert));
      }
    },
    [dispatch, isEmerEnabled]
  );

  usePubSubSocket({ cb: handleAddAlert });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const items = data?.result?.items ?? [];
    const visibleItems = isEmerEnabled
      ? items
      : items.filter((alert) => !isEmerAlertRecord(alert));
    const pageIds = visibleItems.map((alert) => alert.id ?? '');
    const page = {
      alerts: visibleItems,
      totalAlerts: data?.result?.total_items ?? 0,
    };

    // setAlerts only merges, so a page 1 sync evicts what the previous window stored; socket alerts outlive it within an org.
    const storedWindow = storedWindowRef.current;
    if (cursor?.token) {
      dispatch(setAlerts(page));
      storedWindowRef.current = {
        orgId,
        ids: [...new Set([...storedWindow.ids, ...pageIds])],
      };
    } else if (storedWindow.orgId && storedWindow.orgId !== orgId) {
      dispatch(replaceAlerts(page));
      storedWindowRef.current = { orgId, ids: pageIds };
    } else {
      const staleIds = storedWindow.ids.filter((id) => !pageIds.includes(id));
      if (staleIds.length) {
        dispatch(removeAlerts(staleIds));
      }
      dispatch(setAlerts(page));
      storedWindowRef.current = { orgId, ids: pageIds };
    }

    // Hidden alerts shrink the window the unread dot is based on, so page until a full page of visible alerts is seen.
    const nextToken = data?.result?.continuation_token;
    const visibleCount = (cursor?.visibleCount ?? 0) + visibleItems.length;
    if (
      !isEmerEnabled &&
      orgId &&
      firstPageFulfilledAt &&
      nextToken &&
      !isFetching &&
      visibleCount < BADGE_PAGE_SIZE
    ) {
      setBadgeCursor({
        orgId,
        isEmerEnabled,
        firstPageFulfilledAt,
        token: nextToken,
        visibleCount,
      });
    }
  }, [
    isLoading,
    isFetching,
    dispatch,
    data?.result?.items,
    data?.result?.total_items,
    data?.result?.continuation_token,
    isEmerEnabled,
    orgId,
    firstPageFulfilledAt,
    cursor?.token,
    cursor?.visibleCount,
  ]);

  // Alerts stored while the flag was on stay in Redux after it turns off, so drop them.
  useEffect(() => {
    if (isEmerEnabled) {
      return;
    }
    const hiddenIds = storedAlerts
      .filter((alert) => isEmerAlertRecord(alert))
      .map((alert) => alert.id ?? '');
    if (hiddenIds.length) {
      dispatch(removeAlerts(hiddenIds));
    }
  }, [isEmerEnabled, storedAlerts, dispatch]);
};
